/* eslint-disable-next-line no-restricted-imports */
import {
  useLDClient,
  useFlags as useLDFlags,
} from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';

export type FlagsType = Record<string, boolean | string>;

export function useFlag<T = boolean>(flagName: string, defaultValue?: T): T {
  const client = useLDClient();
  // Skip the variation() call in the first useEffect run when the client was
  // already available at mount time, since useState's initializer will have
  // already evaluated it. Without this, LD records two evaluation events for
  // the same flag on mount. Set to false after the first run so that
  // subsequent effect triggers (flagName/defaultValue changes) still evaluate.
  const hadClientOnMount = useRef(!!client);
  const [value, setValue] = useState<T>(
    () => (client?.variation(flagName, defaultValue) ?? defaultValue) as T
  );

  useEffect(() => {
    if (!client) return;

    if (!hadClientOnMount.current) {
      setValue(client.variation(flagName, defaultValue) as T);
    }
    hadClientOnMount.current = false;

    const handler = () =>
      setValue(client.variation(flagName, defaultValue) as T);
    client.on(`change:${flagName}`, handler);
    return () => client.off(`change:${flagName}`, handler);
  }, [client, flagName, defaultValue]);

  const params = new URL(document.location.toString()).searchParams;
  if (params.get('featureFlagsOn')?.split(',').includes(flagName))
    return true as T;
  if (params.get('featureFlagsOff')?.split(',').includes(flagName))
    return false as T;

  return value;
}

export const useFlagsWithoutEvaluation = () => {
  const flagsLD = useLDFlags();
  const params = new URL(document.location.toString()).searchParams;
  const featureFlagsOn = params.get('featureFlagsOn')?.split(',');
  const featureFlagsOff = params.get('featureFlagsOff')?.split(',');

  const flags: FlagsType = useMemo(() => {
    const flagsBase: FlagsType = {};

    if (!flagsLD) return flagsBase;

    Object.keys(flagsLD).forEach((flag) => {
      if (featureFlagsOn?.includes(flag)) {
        flagsBase[flag] = true;
      } else if (featureFlagsOff?.includes(flag)) {
        flagsBase[flag] = false;
      } else {
        flagsBase[flag] = flagsLD[flag];
      }
    });
    return flagsBase;
  }, [flagsLD, featureFlagsOn, featureFlagsOff]);

  return flags;
};
